import { LitElement, html } from "lit";

import { customElement, property } from "lit/decorators.js";

import { styles } from "./content-container.css";

/**
 * Generic container component for backoffice content.
 * Provides consistent column-based layout with maximum 508px width per column.
 *
 * @example
 * ```html
 * <content-container variant="primary">
 *   <div slot="column">First column content</div>
 *   <div slot="column">Second column content</div>
 * </content-container>
 * ```
 */
@customElement("content-container")
export class ContentContainer extends LitElement {
  static override styles = styles;

  /**
   * Visual variant of the container
   * - primary: Bordered container with background (like worlds-layout .main)
   * - secondary: Clean container with padding only (like worlds-layout .secondary)
   */
  @property({ type: String })
  variant: "primary" | "secondary" = "primary";

  @property({ type: Number })
  columns: 1 | 2 = 1;

  protected override render() {
    const containerClasses = ["container", this.variant, this.columns === 2 ? "two-columns" : ""]
      .filter(Boolean)
      .join(" ");

    return html`
      <div class="${containerClasses}">
        <slot name="column" class="column"></slot>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "content-container": ContentContainer;
  }
}
